import * as React from "react";
import { useId, useState, useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";

import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";
import { RichText } from "@learningblocks/shared-components/lib/RichText";

import { MultiSelectQuestionProps, CheckboxState } from "./types";
import { fetchTranslations } from "./locales/config";
import { CheckboxGroup } from "./CheckboxGroup";
import { Figure } from "../Figure";
import { Collapsible } from "../LxCollapsible";
import { LxButton } from "../LxButton";
import { FeedbackBanner } from "../FeedbackBanner";
import { TableQuestion } from "../TableQuestion";
import { useQuestion, QuestionType } from "../QuestionProvider";
import {
  multiSelectQuestionClass,
  containerClass,
  questionSectionClass,
  questionHeaderClass,
  questionCategoryClass,
  questionTextClass,
  selectAllTextClass,
  optionsSectionClass,
  buttonSlabClass,
  attemptsTextClass,
  collapsibleClass,
  feedbackWrapperClass,
} from "./MultiSelectQuestion.css";

/* ---------------------------- Internationalization ---------------------------- */

export const messages = defineMessages({
  attemptsLeft: {
    id: "MultiSelectQuestion.attemptsLeft",
    defaultMessage: "Attempts left: {attemptsLeft}",
    description:
      "Label showing the number of attempts left for the question. {attemptsLeft} is a placeholder for the actual number.",
  },
  submitAnswer: {
    id: "MultiSelectQuestion.submitAnswer",
    defaultMessage: "Submit answer",
    description: "Text for the button to submit the selected answers.",
  },
  submitPrediction: {
    id: "MultiSelectQuestion.submitPrediction",
    defaultMessage: "Submit prediction",
    description: "Text for the button to submit the selected prediction.",
  },
  tryAgain: {
    id: "MultiSelectQuestion.tryAgain",
    defaultMessage: "Try again",
    description: "Text for the button to try again after incorrect submission.",
  },
  resetQuestion: {
    id: "MultiSelectQuestion.reset",
    defaultMessage: "Reset question",
    description: "Text for the button to reset the question and try again.",
  },
  resetSelection: {
    id: "MultiSelectQuestion.predictionReset",
    defaultMessage: "Reset prediction",
    description: "Text for the button to reset the question and try again.",
  },
  resetQuestionAriaLabel: {
    id: "MultiSelectQuestion.resetQuestionAriaLabel",
    defaultMessage: "Reset question button",
    description:
      "Aria label for the button to reset the question and try again.",
  },
  resetPredictionAriaLabel: {
    id: "MultiSelectQuestion.resetPredictionAriaLabel",
    defaultMessage: "Reset prediction button",
    description:
      "Aria label for the button to reset the prediction and try again.",
  },
  predictionSubmitted: {
    id: "MultiSelectQuestion.predictionSubmitted",
    defaultMessage:
      "Prediction submitted. You will compare this answer with your final result at the end of the experiment.",
    description:
      "Text shown in the banner when a prediction question has been submitted.",
  },
  multiSelectHeader: {
    id: "MultiSelectQuestion.multiSelectHeader",
    defaultMessage: "Multi-select Question",
    description: "Header text for multi-select question type.",
  },
  predictionHeader: {
    id: "MultiSelectQuestion.predictionHeader",
    defaultMessage: "Prediction Question",
    description: "Header text for prediction question type.",
  },
  selectAllThatApply: {
    id: "MultiSelectQuestion.selectAllThatApply",
    defaultMessage: "(Select all that apply)",
    description:
      "Instruction text telling users they can select multiple options.",
  },
  correctAnswer: {
    id: "MultiSelectQuestion.correctAnswer",
    defaultMessage: "<b>Correct!</b>",
    description:
      "Feedback message shown when all correct answers are selected.",
  },
  incorrectAnswer: {
    id: "MultiSelectQuestion.incorrectAnswer",
    defaultMessage:
      "<b>Not quite.</b> You haven't selected the correct combination of answers.",
    description: "Feedback message shown when incorrect answers are selected.",
  },
});

/* ---------------------------- UI Subcomponents ---------------------------- */

const QuestionHeader: React.FC<{
  id: string;
}> = ({ id }) => (
  <div className={questionHeaderClass}>
    <p id={id} className={questionCategoryClass}>
      <RichText value={messages.predictionHeader} />
    </p>
  </div>
);

const QuestionFooter: React.FC<{
  attemptsLeft: number;
  showReset: boolean;
  showTryAgain: boolean;
  onSubmit: () => void;
  onReset: () => void;
  onTryAgain: () => void;
  isSubmitDisabled: boolean;
  isPredictionQuestion: boolean;
}> = ({
  attemptsLeft,
  showReset,
  showTryAgain,
  onSubmit,
  onReset,
  onTryAgain,
  isSubmitDisabled,
  isPredictionQuestion,
}) => {
  const { formatMessage } = useIntl();
  const submitTextId = isPredictionQuestion
    ? messages.submitPrediction.id
    : messages.submitAnswer.id;
  const resetTextId = isPredictionQuestion
    ? messages.resetSelection.id
    : messages.resetQuestion.id;
  const resetAriaLabel = formatMessage(
    isPredictionQuestion
      ? messages.resetPredictionAriaLabel
      : messages.resetQuestionAriaLabel
  );
  const submitButtonType = isPredictionQuestion ? "alternative" : "primary";

  return (
    <div className={buttonSlabClass} data-name="Button slab">
      {attemptsLeft > -1 && (
        <p
          className={attemptsTextClass}
          data-testid="attempts-left"
          aria-live="polite"
          role="status"
        >
          <RichText
            value={{
              ...messages.attemptsLeft,
              placeholderValues: { attemptsLeft },
            }}
          />
        </p>
      )}
      {showReset ? (
        <LxButton
          variant="secondary"
          text={resetTextId}
          ariaLabel={resetAriaLabel}
          size="large"
          onClickHandler={onReset}
        />
      ) : showTryAgain ? (
        <LxButton
          variant="alternative"
          text={messages.tryAgain.id}
          size="large"
          onClickHandler={onTryAgain}
          disabled={false}
        />
      ) : (
        <LxButton
          variant={submitButtonType}
          text={submitTextId}
          size="large"
          onClickHandler={onSubmit}
          disabled={isSubmitDisabled}
        />
      )}
    </div>
  );
};

/* ---------------------------- Main Component ---------------------------- */

const MultiSelectQuestionInternal: React.FC<MultiSelectQuestionProps> = ({
  id,
  content,
  question,
  text,
  image,
  dataTable,
  options,
  answerId,
  hintDetails,
  isPredictionQuestion = false,
  totalAttempts = -1,
  onOptionToggle,
  onSubmit,
  onReset,
  setCompleted,
}) => {
  // Use the question hook - handles both provider and local state
  const questionState = useQuestion({
    componentId: id,
    type: QuestionType.MultiSelect,
  });

  const { selectedOptionIds, submittedOptionIds } = questionState;

  // Component-specific state
  const [attemptsLeft, setAttemptsLeft] = useState<number>(totalAttempts);
  const [resetCount, setResetCount] = useState(0);

  const elementId = useId();
  const headerId = `${elementId}-header`;
  const textId = `${elementId}-text`;
  const questionId = `${elementId}-question`;
  const imageId = `${elementId}-image`;
  const imageCaptionId = `${elementId}-caption`;

  /* ---------------------------- Derived Booleans ---------------------------- */

  const hasSubmittedAnswers = submittedOptionIds.length > 0;

  // Check if all correct answers are in the submitted list
  const hasAllCorrectAnswers = answerId.every((id) =>
    submittedOptionIds.includes(id)
  );

  // Check if any incorrect answers are in the submitted list
  const hasIncorrectAnswers = submittedOptionIds.some(
    (id) => !answerId.includes(id)
  );

  // Question is complete when all correct answers are submitted and no incorrect ones
  const hasCorrectAnswer = hasAllCorrectAnswers && !hasIncorrectAnswers;

  const showTryAgainButton =
    !isPredictionQuestion &&
    hasSubmittedAnswers &&
    !hasCorrectAnswer &&
    (attemptsLeft > 0 || attemptsLeft === -1);

  const showResetButton = isPredictionQuestion
    ? hasSubmittedAnswers
    : hasSubmittedAnswers && hasCorrectAnswer;

  const showPredictionFeedback = isPredictionQuestion && hasSubmittedAnswers;

  const showAssessmentFeedback =
    !isPredictionQuestion && hasSubmittedAnswers && !hasCorrectAnswer;
  const showSuccessFeedback =
    !isPredictionQuestion && hasSubmittedAnswers && hasCorrectAnswer;

  const isSubmitDisabled =
    selectedOptionIds.length === 0 ||
    (hasSubmittedAnswers &&
      !hasCorrectAnswer &&
      arraysEqual(selectedOptionIds.sort(), submittedOptionIds.sort()));

  const isOptionSelectionDisabled =
    (hasCorrectAnswer && !isPredictionQuestion) ||
    (isPredictionQuestion && hasSubmittedAnswers) ||
    attemptsLeft === 0;

  const isCompleted =
    (hasCorrectAnswer && !isPredictionQuestion) ||
    (isPredictionQuestion && hasSubmittedAnswers) ||
    (attemptsLeft === 0 && hasSubmittedAnswers);

  // Notify completion
  useEffect(() => {
    if (isCompleted) {
      setCompleted?.(true);
    }
  }, [isCompleted, setCompleted]);

  const handleOptionToggleWithTryAgain = (optionId: number) => {
    if (showTryAgainButton) {
      questionState.tryAgain();
    }
    questionState.toggleOption(optionId);
    onOptionToggle?.(optionId);
  };

  /* ---------------------------- Helper Functions ---------------------------- */

  function arraysEqual(a: number[], b: number[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  }

  const getCheckboxState = (optionId: number): CheckboxState => {
    if (isOptionSelectionDisabled && hasCorrectAnswer) {
      const isCorrect = answerId.includes(optionId);
      return isCorrect ? "correct" : "default";
    }

    if (isPredictionQuestion && hasSubmittedAnswers) {
      const isSubmitted = submittedOptionIds.includes(optionId);
      return isSubmitted ? "selected" : "default";
    }

    if (isOptionSelectionDisabled) return "disabled";

    const isSelected = selectedOptionIds.includes(optionId);
    return isSelected ? "selected" : "default";
  };

  /* ---------------------------- Handlers ---------------------------- */

  const handleSubmit = () => {
    if (selectedOptionIds.length > 0) {
      const allCorrectSelected = answerId.every((id) =>
        selectedOptionIds.includes(id)
      );
      const noIncorrectSelected = selectedOptionIds.every((id) =>
        answerId.includes(id)
      );
      const isCorrectAnswer = allCorrectSelected && noIncorrectSelected;
      const willRunOutOfAttempts = attemptsLeft === 1;

      const willBeCompleted =
        isCorrectAnswer || willRunOutOfAttempts || isPredictionQuestion;

      setAttemptsLeft((prev) => {
        if (prev > 0) return prev - 1;
        if (prev === -1) return -1;
        return 0;
      });

      questionState.submit();

      if (willBeCompleted) {
        setCompleted?.(true);
      }

      onSubmit?.();
    }
  };

  const handleReset = () => {
    questionState.reset();
    setAttemptsLeft(totalAttempts);
    setResetCount((prev) => prev + 1);
    onReset?.();
  };

  const handleTryAgain = () => {
    questionState.tryAgain();
    onReset?.();
  };

  /* ---------------------------- Dynamic Content ---------------------------- */

  const buildAriaLabelledby = () => {
    const ids: string[] = [headerId];
    if (text) ids.push(textId);
    ids.push(questionId);
    if (image) ids.push(imageId);
    if (image?.caption) ids.push(imageCaptionId);
    return ids.join(" ");
  };

  const ariaLabelledby = buildAriaLabelledby();

  const renderContent = () => {
    const elements: React.ReactNode[] = [];

    content.forEach((type, index) => {
      switch (type) {
        case "text":
          if (text) {
            elements.push(
              <div
                key={`text-${index}`}
                id={textId}
                className={questionTextClass}
              >
                <RichText value={text} />
              </div>
            );
          }
          break;
        case "question":
          elements.push(
            <div
              key={`q-${index}`}
              className={questionSectionClass}
              data-name="Question"
            >
              {isPredictionQuestion && <QuestionHeader id={headerId} />}
              <div id={questionId} className={questionTextClass}>
                <RichText value={question} />
              </div>
            </div>
          );
          break;
        case "image":
          if (image) {
            elements.push(
              <Figure
                key={`img-${index}`}
                src={image.src}
                altText={image.altText}
                caption={image.caption}
                imageId={imageId}
                captionId={image.caption ? imageCaptionId : undefined}
                containerBgColor={image.containerBgColor}
              />
            );
          }
          break;
        case "dataTable":
          if (dataTable) {
            elements.push(
              <TableQuestion
                key={`table-${index}`}
                id={index}
                type="DATA_TABLE"
                data={dataTable.data}
                content={["table"]}
                isTransparent={true}
              />
            );
          }
          break;
        case "options":
          elements.push(
            <p key={`select-all-${index}`} className={selectAllTextClass}>
              <RichText value={messages.selectAllThatApply} />
            </p>
          );
          elements.push(
            <div
              key={`opt-${index}`}
              className={optionsSectionClass}
              data-name="Options"
            >
              <CheckboxGroup
                options={options}
                selectedOptionIds={selectedOptionIds}
                submittedOptionIds={submittedOptionIds}
                onOptionToggle={
                  isOptionSelectionDisabled
                    ? () => {
                        /* no-op */
                      }
                    : handleOptionToggleWithTryAgain
                }
                getCheckboxState={getCheckboxState}
                ariaLabelledby={ariaLabelledby}
                resetCount={resetCount}
                isPredictionQuestion={isPredictionQuestion}
              />
            </div>
          );
          break;
        case "hintDetails":
          if (hintDetails) {
            elements.push(
              <div key={`hint-${index}`} className={collapsibleClass}>
                <Collapsible
                  label={hintDetails.label}
                  content={hintDetails.content}
                  open={hintDetails.open}
                />
              </div>
            );
          }
          break;
        default:
          break;
      }
    });

    return elements;
  };

  /* ---------------------------- Render ---------------------------- */

  return (
    <div className={multiSelectQuestionClass} data-name="Multi-select question">
      <div className={containerClass}>
        {renderContent()}
        {(showSuccessFeedback || showAssessmentFeedback) && (
          <div
            className={feedbackWrapperClass}
            aria-live="polite"
            role="status"
            key={`feedback-${resetCount}`}
          >
            {showSuccessFeedback && (
              <FeedbackBanner
                variant="success"
                message={messages.correctAnswer}
              />
            )}
            {showAssessmentFeedback && (
              <FeedbackBanner
                variant="fail"
                message={messages.incorrectAnswer}
              />
            )}
          </div>
        )}
        {showPredictionFeedback && (
          <div
            className={feedbackWrapperClass}
            aria-live="polite"
            role="status"
            key={`prediction-feedback-${resetCount}`}
          >
            <FeedbackBanner
              variant="information"
              message={messages.predictionSubmitted}
            />
          </div>
        )}
        <QuestionFooter
          attemptsLeft={attemptsLeft}
          showReset={showResetButton}
          showTryAgain={showTryAgainButton}
          onSubmit={handleSubmit}
          onReset={handleReset}
          onTryAgain={handleTryAgain}
          isSubmitDisabled={isSubmitDisabled}
          isPredictionQuestion={isPredictionQuestion}
        />
      </div>
    </div>
  );
};

export const MultiSelectQuestion = (props: MultiSelectQuestionProps) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <MultiSelectQuestionInternal {...props} />
  </TranslationsProvider>
);
